import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import FullScreenDialog from "@/modules/common/components/modals/full-screen-modal";
import Task from "@/modules/lead-page/new/components/task-management/next-action-item";
import GenericAccordion from "@/modules/common/components/ui-kit/generic-accordion";
import PlusIcon from "@/modules/common/components/ui-kit/icons/plus-icon";
import DateTimePickerValue from "@/modules/common/components/ui/date-time-picker";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import useAddNextActions from "@/modules/lead-page/hooks/next-actions/add-next-actions.hook";
import useDeleteNextAction from "@/modules/lead-page/hooks/next-actions/delete-next-actions.hook";
import useGetNextActions from "@/modules/lead-page/hooks/next-actions/get-next-actions.hook";
import NextActionsSkeleton from "@/modules/lead-page/new/components/task-management/next-actions-skeleton";
import { NwButton } from "@nawy/ui";

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback } from "react";

const NextActions = () => {
  const theme = useTheme();

  const screen = useScreenSize();

  const { nextActionsData, isLoading } = useGetNextActions();

  const {
    setAddNextActionModal,
    handleDateChange,
    handleSubmitNextAction,
    handleTextareaChange,
    addNextActionModal,
    dateValue,
    description,
    isloading,
    dateError,
    notify,
    setNotify,
    setDateError,
    setDescription,
    setDate,
  } = useAddNextActions();

  const {
    nextActionId,
    setNextActionId,
    setDialogOpenConfirm,
    handleDeleteDialog,
    handleDeleteNextAction,
    dialogOpenConfirm,
    loadDeleting,
  } = useDeleteNextAction();

  const handleCloseDialog = () => {
    setDialogOpenConfirm(false);
    setNextActionId(undefined);
  };

  const renderComponent = useCallback(
    () => (
      <Box
        sx={{
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.lg,
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          Description
          <TextField
            required
            id="outlined-multiline-flexible"
            placeholder=""
            multiline
            fullWidth
            sx={{
              marginTop: theme.nawy.spacing.xxs,
            }}
            rows={3}
            onChange={handleTextareaChange}
          />
        </Typography>

        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          Date
          <DateTimePickerValue
            value={dateValue}
            onChange={handleDateChange}
            error={dateError}
            setError={setDateError}
          />
        </Typography>

        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.NOTIFY_ASSIGNED_AGENT,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <FormGroup>
            <FormControlLabel
              color="#616161"
              control={
                <Checkbox
                  sx={{ color: "#D0D5DD" }}
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
              }
              label={
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.md.semibold,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  Notify Assigned Agent
                </Typography>
              }
            />
          </FormGroup>
        </PermissionGuard>
      </Box>
    ),
    [
      dateError,
      dateValue,
      theme,
      handleDateChange,
      handleTextareaChange,
      notify,
      setDateError,
      setNotify,
    ]
  );

  const renderActionsList = useCallback(() => {
    if (isLoading) {
      return <NextActionsSkeleton />;
    }

    if (!nextActionsData || nextActionsData.length === 0) {
      return (
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          No next actions yet
        </Typography>
      );
    }

    return nextActionsData?.map((action) => (
      <Task
        key={action.uid}
        nextActionId={nextActionId}
        action={action}
        handleMarkComplete={handleDeleteDialog}
      />
    ));
  }, [isLoading, nextActionsData, theme, handleDeleteDialog, nextActionId]);

  const renderNextActionsWrapper = useCallback(() => {
    return (
      <GenericAccordion
        title="Next Actions"
        id="next-actions"
        defaultExpanded
        rounded
      >
        <Stack direction="column" spacing={theme.nawy.spacing.md}>
          {renderActionsList()}

          <NwButton
            variant="tonal"
            onClick={() => setAddNextActionModal(true)}
            startIcon={<PlusIcon />}
            sx={{
              display: "flex",
              width: "fit-content",
              borderRadius: 2,
              height: "36px",
              whiteSpace: "nowrap",
              padding: theme.nawy.spacing.md,
              justifyContent: "center",
              alignItems: "center",
              textTransform: "none",
              backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
              color: theme.nawy.colors.semantic.text.accent,
              border: `1px solid ${theme.nawy.colors.semantic.layer.accentLight}`,
              gap: 0,
              "& .MuiButton-startIcon": {
                marginRight: `4px !important`,
                marginLeft: "0 !important",
              },
              "&:hover": {
                backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
                opacity: 0.9,
              },
          }}
          >
            New Action
          </NwButton>
        </Stack>
      </GenericAccordion>
    );
  }, [renderActionsList, setAddNextActionModal, theme]);

  return (
    <>
      {renderNextActionsWrapper()}

      <ConfirmationModal
        open={dialogOpenConfirm}
        setOpen={() => setDialogOpenConfirm(false)}
        onConfirmHandler={() => handleDeleteNextAction(nextActionId)}
        title="Complete Next Action"
        body="Are you sure you want to mark this action as done?"
        isLoading={loadDeleting}
        onCancelHandler={handleCloseDialog}
      />

      {screen.isMobile ? (
        <FullScreenDialog
          open={addNextActionModal}
          setOpen={setAddNextActionModal}
          header="Create a New Next Action"
          handleConfirm={handleSubmitNextAction}
          disabledConfirmButton={description && !dateError ? false : true}
          modalFooter
          handleCloseModal={() => {
            setDate(""), setDescription("");
          }}
          isLoading={isloading}
          cancelText="Discard"
          enableEnterKeySubmit={false}
        >
          {renderComponent()}
        </FullScreenDialog>
      ) : (
        <EditFieldModal
          open={addNextActionModal}
          setOpen={setAddNextActionModal}
          header="Create a New Next Action"
          width={400}
          isLoading={isloading}
          handleCloseModal={() => {
            setDate(""), setDescription("");
          }}
          handleConfirm={() => handleSubmitNextAction()}
          disabledConfirmButton={description && !dateError ? false : true}
          cancelText="Discard"
        >
          {renderComponent()}
        </EditFieldModal>
      )}
    </>
  );
};

export default NextActions;
